import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSignOutAlt,
  FaBriefcase,
  FaCheckCircle,
  FaTimesCircle,
  FaTimes,
  FaSave,
  FaHome
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useJobs } from '../context/JobsContext';
import '../styles/Admin.css';

const currencies = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'CLP', name: 'Chilean Peso', symbol: '$' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'KD' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'QR' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'SR' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'AED' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'ARS', name: 'Argentine Peso', symbol: '$' },
  { code: 'COP', name: 'Colombian Peso', symbol: '$' },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn' },
  { code: 'ISK', name: 'Icelandic Krona', symbol: 'kr' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'MAD' },
  { code: 'TWD', name: 'Taiwan Dollar', symbol: 'NT$' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs' },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: 'रू' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'JD' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'BD' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'OMR' }
];

const initialJobForm = {
  title: '',
  department: '',
  location: '',
  type: 'Full-time',
  description: '',
  image: '',
  payRate: '',
  currency: 'USD',
  vacancy: ''
};

const AdminDashboard = () => {
  const { logout } = useAuth();
  const { jobs, addJob, updateJob, deleteJob, toggleJobStatus } = useJobs();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobForm, setJobForm] = useState(initialJobForm);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const openAddModal = () => {
    setEditingJob(null);
    setJobForm(initialJobForm);
    setShowModal(true);
  };

  const openEditModal = (job) => {
    setEditingJob(job);
    setJobForm({
      title: job.title || '',
      department: job.department || '',
      location: job.location || '',
      type: job.type || 'Full-time',
      description: job.description || '',
      image: job.image || '',
      payRate: job.payRate || '',
      currency: job.currency || 'USD',
      vacancy: job.vacancy || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingJob(null);
    setJobForm(initialJobForm);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setJobForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingJob) {
      await updateJob(editingJob.id, jobForm);
    } else {
      await addJob(jobForm);
    }

    closeModal();
  };

  const handleDelete = async (id) => {
    await deleteJob(id);
    setDeleteConfirm(null);
  };

  const activeJobsCount = jobs.filter(job => job.isActive).length;
  const inactiveJobsCount = jobs.filter(job => !job.isActive).length;

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-logo">
            <span className="admin-logo-text">JWCS</span>
            <span className="admin-logo-subtext">Admin Portal</span>
          </div>
        </div>
        <div className="admin-header-right">
          <button className="admin-nav-btn" onClick={() => navigate('/')}>
            <FaHome /> View Site
          </button>
          <button className="admin-nav-btn logout" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-container">
          <div className="admin-welcome">
            <h1>Job Management</h1>
            <p>Manage career opportunities displayed on the website</p>
          </div>

          <div className="admin-stats">
            <div className="stat-card">
              <div className="stat-icon total">
                <FaBriefcase />
              </div>
              <div className="stat-info">
                <span className="stat-number">{jobs.length}</span>
                <span className="stat-label">Total Jobs</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon active">
                <FaCheckCircle />
              </div>
              <div className="stat-info">
                <span className="stat-number">{activeJobsCount}</span>
                <span className="stat-label">Active Jobs</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon inactive">
                <FaTimesCircle />
              </div>
              <div className="stat-info">
                <span className="stat-number">{inactiveJobsCount}</span>
                <span className="stat-label">Inactive Jobs</span>
              </div>
            </div>
          </div>

          <div className="admin-actions">
            <button className="btn btn-primary" onClick={openAddModal}>
              <FaPlus /> Add New Job
            </button>
          </div>

          <div className="jobs-table-container">
            <table className="jobs-table">
              <thead>
                <tr>
                  <th>Job Title</th>
                  <th>Department</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state">
                      No jobs found. Click "Add New Job" to create one.
                    </td>
                  </tr>
                ) : (
                  jobs.map(job => (
                    <tr key={job.id} className={!job.isActive ? 'inactive-row' : ''}>
                      <td className="job-title-cell">
                        <strong>{job.title}</strong>
                      </td>
                      <td>{job.department}</td>
                      <td>{job.location}</td>
                      <td>{job.type}</td>
                      <td>
                        <button
                          className={`status-badge ${job.isActive ? 'active' : 'inactive'}`}
                          onClick={() => toggleJobStatus(job.id)}
                          title={job.isActive ? 'Click to deactivate' : 'Click to activate'}
                        >
                          {job.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="actions-cell">
                        <button
                          className="action-btn edit"
                          onClick={() => openEditModal(job)}
                          title="Edit job"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => setDeleteConfirm(job.id)}
                          title="Delete job"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add/Edit Job Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingJob ? 'Edit Job' : 'Add New Job'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="title">Job Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={jobForm.title}
                  onChange={handleFormChange}
                  placeholder="e.g., Senior Recruitment Consultant"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="department">Department</label>
                  <input
                    type="text"
                    id="department"
                    name="department"
                    value={jobForm.department}
                    onChange={handleFormChange}
                    placeholder="e.g., Human Resources"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="location">Location</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={jobForm.location}
                    onChange={handleFormChange}
                    placeholder="e.g., Remote / Hybrid"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="type">Job Type</label>
                  <select
                    id="type"
                    name="type"
                    value={jobForm.type}
                    onChange={handleFormChange}
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="vacancy">Number of Vacancies</label>
                  <input
                    type="number"
                    id="vacancy"
                    name="vacancy"
                    value={jobForm.vacancy}
                    onChange={handleFormChange}
                    placeholder="e.g., 25"
                    min="1"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="payRate">Pay Rate (per hour)</label>
                  <input
                    type="number"
                    id="payRate"
                    name="payRate"
                    value={jobForm.payRate}
                    onChange={handleFormChange}
                    placeholder="e.g., 25.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="currency">Currency</label>
                  <select
                    id="currency"
                    name="currency"
                    value={jobForm.currency}
                    onChange={handleFormChange}
                  >
                    {currencies.map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.code} - {curr.name} ({curr.symbol})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="image">Job Image URL</label>
                <input
                  type="text"
                  id="image"
                  name="image"
                  value={jobForm.image}
                  onChange={handleFormChange}
                  placeholder="e.g., /images/jobs/job-name.jpg or https://example.com/image.jpg"
                />
                <small className="form-hint">Enter the image path or URL for this job listing</small>
              </div>

              <div className="form-group">
                <label htmlFor="description">Job Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={jobForm.description}
                  onChange={handleFormChange}
                  placeholder="Enter a detailed job description..."
                  rows="4"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <FaSave /> {editingJob ? 'Update Job' : 'Create Job'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this job? This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                <FaTrash /> Delete Job
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
